import { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { Plus, Trash2, UsersRound } from 'lucide-react';

import { db } from '../../core/firebase/firebase';
import { useSchoolClasses } from '../../core/school/classes';

const emptyChild = () => ({
  temporaryId: crypto.randomUUID?.() || `child_${Date.now()}_${Math.random()}`,
  fullName: '',
  requestedClassId: '',
  requestedLevel: ''
});

const initialForm = () => ({
  parentName: '',
  phone: '',
  email: '',
  schoolYear: '2026–2027',
  message: '',
  children: [emptyChild()]
});


export default function PublicRegistrationPage() {
  const { classes } = useSchoolClasses();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function addChild() {
    setForm((current) => ({ ...current, children: [...current.children, emptyChild()] }));
  }

  function updateChild(id, field, value) {
    setForm((current) => ({
      ...current,
      children: current.children.map((child) => (
        child.temporaryId === id ? { ...child, [field]: value } : child
      ))
    }));
  }

  function removeChild(id) {
    setForm((current) => ({
      ...current,
      children: current.children.length === 1
        ? current.children
        : current.children.filter((child) => child.temporaryId !== id)
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');

    const children = form.children
      .map((child) => ({
        ...child,
        fullName: child.fullName.trim(),
        requestedClassId: child.requestedClassId || '',
        requestedLevel: child.requestedLevel.trim()
      }))
      .filter((child) => child.fullName && child.requestedClassId);

    if (!form.parentName.trim() || !form.phone.trim() || children.length !== form.children.length) {
      setError('Veuillez renseigner le nom et le niveau de chaque enfant.');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'demandes_inscription'), {
        schemaVersion: 2,
        applicationType: 'family',
        guardian: {
          fullName: form.parentName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          cin: '',
          profession: '',
          relationship: 'parent',
          address: ''
        },
        children: children.map((child) => ({
          temporaryId: child.temporaryId,
          fullName: child.fullName,
          requestedClassId: child.requestedClassId,
          requestedLevel: child.requestedLevel,
          birthDate: '',
          previousSchool: '',
          alreadyEnrolled: false,
          hasSiblingsAtSchool: children.length > 1,
          difficulties: '',
          notes: '',
          schoolYear: form.schoolYear
        })),
        message: form.message.trim(),
        source: 'public',
        status: 'nouvelle',
        notes: [],
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      setForm(initialForm());
    } catch (err) {
      console.error(err);
      setError("La demande n’a pas pu être envoyée. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="registration-screen-public">
      <section className="registration-card-public">
        <Link to="/login" className="registration-back-link-public">
          ← Retour à la connexion
        </Link>

        <div className="registration-heading-public">
          <img className="portal-logo" src="/logo.png" alt="Groupe Scolaire Le Défi" />
          <h1>Demander une inscription</h1>
          <p>
            Remplissez ce petit formulaire. Vous pouvez ajouter tous les enfants de la
            famille concernés par la demande. L’administration vous contactera ensuite
            pour finaliser l’inscription.
          </p>
        </div>

        {success ? (
          <div className="registration-success-public">
            <div className="registration-success-icon">✓</div>
            <h2>Demande envoyée</h2>
            <p>
              Votre demande familiale a bien été enregistrée. L’administration de
              l’école vous contactera prochainement.
            </p>
            <Link to="/login" className="portal-primary-button registration-return-button">
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <form className="registration-form-public" onSubmit={submit}>
            <div className="registration-grid-public">
              <label>
                Nom et prénom du parent
                <input
                  value={form.parentName}
                  onChange={(event) => update('parentName', event.target.value)}
                  placeholder="Ex. Ahmed Ben Ali"
                  required
                />
              </label>

              <label>
                Téléphone
                <input
                  value={form.phone}
                  onChange={(event) => update('phone', event.target.value)}
                  placeholder="Ex. 58 441 534"
                  required
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => update('email', event.target.value)}
                  placeholder="Ex. parent@email.com"
                />
              </label>

              <label>
                Année scolaire
                <select
                  value={form.schoolYear}
                  onChange={(event) => update('schoolYear', event.target.value)}
                >
                  <option>2026–2027</option>
                  <option>2027–2028</option>
                </select>
              </label>
            </div>

            <section className="family-section">
              <div className="family-section-title">
                <div>
                  <span className="family-step">2</span>
                  <div>
                    <h3>Enfant{form.children.length > 1 ? 's' : ''} à inscrire</h3>
                    <p>Un seul dossier familial peut contenir plusieurs enfants.</p>
                  </div>
                </div>
                <button type="button" className="button button-secondary" onClick={addChild}>
                  <Plus size={16} /> Ajouter un autre enfant
                </button>
              </div>

              <div className="children-editor">
                {form.children.map((child, index) => (
                  <article className="child-editor-card" key={child.temporaryId}>
                    <header>
                      <div>
                        <UsersRound size={18} />
                        <strong>Enfant {index + 1}</strong>
                      </div>
                      {form.children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(child.temporaryId)}
                          aria-label={`Retirer l’enfant ${index + 1}`}
                        >
                          <Trash2 size={17} />
                        </button>
                      )}
                    </header>

                    <div className="registration-grid-public">
                      <label>
                        Nom et prénom de l’élève
                        <input
                          value={child.fullName}
                          onChange={(event) => updateChild(child.temporaryId, 'fullName', event.target.value)}
                          placeholder="Ex. Youssef Ben Ali"
                          required
                        />
                      </label>

                      <label>
                        Niveau demandé
                                        <select
                  value={child.requestedClassId}
                  onChange={(event) => {
                    const classId = event.target.value;
                    const selectedClass = classes.find((item) => item.id === classId);
                    updateChild(child.temporaryId, 'requestedClassId', classId);
                    updateChild(child.temporaryId, 'requestedLevel', selectedClass?.name || '');
                  }}
                  required
                >
                  <option value="">Choisir un niveau</option>
                  {classes.map((schoolClass) => <option key={schoolClass.id} value={schoolClass.id}>{schoolClass.name || schoolClass.id}</option>)}
                </select>
                      </label>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <label className="registration-message-public">
              Message complémentaire
              <textarea
                value={form.message}
                onChange={(event) => update('message', event.target.value)}
                placeholder="Ajoutez ici une remarque, une question ou une information importante..."
              />
            </label>

            {error && <div className="portal-alert">{error}</div>}

            <button className="portal-primary-button registration-submit-public" disabled={submitting}>
              {submitting ? 'Envoi en cours…' : 'Envoyer la demande'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
